import { Button, Icon, IconButton } from '@blend-ed/blendx-ui';
import { getConfig } from '@edx/frontend-platform';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { sendMessageTutor } from "../../../api/AICanvasAssistant";
import AiTutor from './components/AiTutor';
import useFetchAiCanvasChatHistory from '../../../hooks/aiChat/useFetchAiCanvasChatHistory';
import { AppContext } from "@edx/frontend-platform/react";
import { useFetchAiCanvasConversationStarter } from '../../../hooks/aiChat/useFetchAiCanvasConversationSarter';
import useIframeContentProcessor from '../../../hooks/useIframeContentProcessor';

function LearningView({ onClose, intl, learningPath }) {
  // Track the index of the current unit in the learning path
  const { authenticatedUser } = useContext(AppContext);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isDissolving, setIsDissolving] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [conversationStartersQuestions, setConversationStartersQuestions] = useState([]);
  const [conversationStartersTopics, setConversationStartersTopics] = useState([]);

  const { data: aiCanvasChatHistory, loading, error } = useFetchAiCanvasChatHistory(selectedTopic?.unit_id, authenticatedUser?.id);
  const { data: aiCanvasConversationStarter, isLoading: aiCanvasConversationStarterLoading, error: aiCanvasConversationStarterError, } = useFetchAiCanvasConversationStarter(selectedTopic?.unit_id);

  // Update conversation starters when data is fetched
  useEffect(() => {
    if (aiCanvasConversationStarter) {
      setConversationStartersQuestions(aiCanvasConversationStarter.questions || []);
      setConversationStartersTopics(aiCanvasConversationStarter.topics || []);
    }
  }, [aiCanvasConversationStarter]);

  // Set the first unit as the selected topic when component loads
  useEffect(() => {
    if (learningPath && learningPath.length > 0) {
      setSelectedTopic(learningPath[currentIndex]);
    }
  }, [learningPath, currentIndex]);

  useEffect(() => {
    if (aiCanvasChatHistory) {
      setMessages(aiCanvasChatHistory.map(message => ({
        id: message.id,
        type: message.role === 'user' ? 'user' : 'ai',
        content: message.message,
        contentType: 'text',
        createdAt: message.createdAt
      })));
    }
  }, [aiCanvasChatHistory, loading]);


  const sendMessageMutation = useReactQueryMutation({
    mutationFn: (message) => sendMessageTutor(message, selectedTopic.unit_id),
    onSuccess: (data) => {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'ai',
        content: data.message,
        contentType: 'text',
        createdAt: new Date().toISOString()
      }]);
    }
  });

  const handleSendMessage = (textOverride) => {
    console.log("handleSendMessage", textOverride || inputText);
    const messageText = textOverride || inputText;

    if (!messageText.trim()) return;

    // Add user message
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content: messageText.trim(),
      contentType: 'text',
      createdAt: new Date().toISOString()
    }]);

    if (!textOverride) {
      setInputText('');
    }

    // Send message to AI and handle response
    sendMessageMutation.mutate(messageText.trim());
  };

  const handleClose = () => {
    setIsDissolving(true);
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };

  // Navigate to the next unit in the learning path
  const handleNextUnit = () => {
    if (currentIndex < learningPath.length - 1) {
      setCurrentIndex(prevIndex => prevIndex + 1);
      // Clear conversation starters when navigating
      setConversationStartersQuestions([]);
      setConversationStartersTopics([]);
    }
  };

  // Navigate to the previous unit in the learning path
  const handlePreviousUnit = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prevIndex => prevIndex - 1);
      // Clear conversation starters when navigating
      setConversationStartersQuestions([]);
      setConversationStartersTopics([]);
    }
  };

  // Get the current unit information for display
  const currentUnit = selectedTopic ? {
    title: selectedTopic.course_name,
    unitTitle: selectedTopic.unit.split(' > ')[selectedTopic.unit.split(' > ').length - 1],
    unitSubtitle: selectedTopic.unit,
    unitNumber: currentIndex + 1,
    totalUnits: learningPath.length,
    unitId: selectedTopic.unit_id,
    module: selectedTopic.module,
    unitPath: selectedTopic.unit
  } : { title: "", unitNumber: 0, totalUnits: 0, unitId: "", module: "", unitPath: "" };

  const iframeUrl = `${getConfig().LMS_BASE_URL}/xblock/${currentUnit.unitId}?show_title=0&show_bookmark=0&recheck_access=1&view=student_view&auto_height=true`;
  const { contentHeight } = useIframeContentProcessor(iframeUrl);

  return (
    <div className={`learning-view ${isDissolving ? 'dissolving' : ''}`}>
      <div className="learning-view__container">
        <div className="learning-view__content">
          <div className="learning-view__header">
            <div className="learning-view__title">
              <div className="learning-view__title-text">
                {learningPath.length > 1 ? currentUnit.title : currentUnit.unitTitle}
                {learningPath.length > 1 && <span className="learning-view__unit">Unit {currentUnit.unitNumber} of {currentUnit.totalUnits}</span>}
              </div>
              <div className="learning-view__unit">{currentUnit.unitSubtitle}</div>
            </div>
            <IconButton
              icon="close"
              iconType="line"
              variant="ghost-gray"
              onClick={handleClose}
              className="learning-view__close"
            />
          </div>
          <div className="learning-view__main">
            {selectedTopic && (
              <iframe
                title={currentUnit.title}
                src={iframeUrl}
                allow="microphone *; camera *; midi *; geolocation *; encrypted-media *, clipboard-write *"
                allowFullScreen
                style={{
                  width: '100%',
                  height: contentHeight ? contentHeight : '10rem',
                  border: 'none',
                  overflow: 'hidden'
                }}
                scrolling="no"
                referrerPolicy="origin"
                className="learning-view__iframe"
              />
            )}
          </div>

          {learningPath.length > 1 && <div className="learning-view__footer">
            <div className="learning-view__footer-text">
              Unit {currentUnit.unitNumber} of {currentUnit.totalUnits}
            </div>
            <div className="learning-view__footer-buttons">
              <Button
                variant="outline-gray"
                leftIcon={<Icon icon="arrow-left" type="line" />}
                size="sm"
                onClick={handlePreviousUnit}
                disabled={currentIndex === 0}
              >
                Previous Unit
              </Button>
              <Button
                variant="primary"
                rightIcon={<Icon icon="arrow-right" type="line" />}
                size="sm"
                onClick={handleNextUnit}
                disabled={currentIndex === learningPath.length - 1}
              >
                Next Unit
              </Button>
            </div>
          </div>}
        </div>
        <AiTutor
          conversationStartersQuestions={conversationStartersQuestions}
          conversationStartersTopics={conversationStartersTopics}
          messages={messages}
          inputText={inputText}
          setInputText={setInputText}
          handleSendMessage={handleSendMessage}
          isLoading={sendMessageMutation.isLoading || aiCanvasConversationStarterLoading}
          intl={intl}
        />
      </div>
    </div>
  );
}

LearningView.propTypes = {
  intl: intlShape.isRequired,
  learningPath: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default injectIntl(LearningView); 